import { LongyLogo } from '@longy/web-components'
import { Award, Mail, MapPin, Phone, Share2, ShieldCheck, Video } from 'lucide-react'

import { useWebContent } from '../../hooks/useWebContent'
import { richTextListClassName } from '../../utils/richText'

const quickLinks = [
    { href: '/packages', key: 'quickLink1' },
    { href: '#testimonials', key: 'quickLink2' },
    { href: '/knowledge-base', key: 'quickLink3' },
    { href: '/events', key: 'quickLink4' },
]

const infoLinks = [
    { href: '/about', key: 'infoLink1' },
    { href: '/contact', key: 'infoLink2' },
    { href: '/terms', key: 'infoLink3' },
    { href: '/privacy-policy', key: 'infoLink4' },
]

export const Footer = () => {
    const { getText } = useWebContent('FOOTER')
    const email = getText('email')
    const phone = getText('phone')
    const address = getText('address')

    return (
        <footer id="contact" className="bg-brand-dark py-24 text-white">
            <div className="mx-auto mb-24 grid max-w-[1280px] grid-cols-1 gap-16 px-6 md:grid-cols-2 lg:grid-cols-4">
                {/* Brand */}
                <div>
                    <div className="mb-10 flex justify-center">
                        <LongyLogo className="size-36" />
                    </div>
                    <div
                        dangerouslySetInnerHTML={{ __html: getText('description') }}
                        className={`mb-8 text-sm leading-relaxed text-slate-400 ${richTextListClassName}`}
                    />
                    <div className="flex gap-4">
                        <a
                            href="#"
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-all hover:border-primary hover:bg-primary"
                        >
                            <Share2 className="h-5 w-5" />
                        </a>
                        <a
                            href="#"
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-all hover:border-primary hover:bg-primary"
                        >
                            <Video className="h-5 w-5" />
                        </a>
                    </div>
                </div>

                {/* Quick Links */}
                <div>
                    <h6 className="mb-10 text-lg font-bold text-brand-yellow">
                        {getText('quickLinksTitle')}
                    </h6>
                    <ul className="space-y-5 text-sm text-slate-400">
                        {quickLinks.map((link) => (
                            <li key={link.key}>
                                <a
                                    href={link.href}
                                    className="transition-colors hover:text-primary"
                                >
                                    {getText(link.key)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Info Links */}
                <div>
                    <h6 className="mb-10 text-lg font-bold text-brand-yellow">
                        {getText('infoTitle')}
                    </h6>
                    <ul className="space-y-5 text-sm text-slate-400">
                        {infoLinks.map((link) => (
                            <li key={link.key}>
                                <a
                                    href={link.href}
                                    className="transition-colors hover:text-primary"
                                >
                                    {getText(link.key)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Contact */}
                <div>
                    <h6 className="mb-10 text-lg font-bold text-brand-yellow">
                        {getText('contactTitle')}
                    </h6>
                    <ul className="space-y-5 text-sm text-slate-400">
                        {email && (
                            <li className="flex items-start gap-3">
                                <Mail className="h-5 w-5 text-primary" />
                                {email}
                            </li>
                        )}
                        {phone && (
                            <li className="flex items-start gap-3">
                                <Phone className="h-5 w-5 text-primary" />
                                <a
                                    href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                                    className="transition-colors hover:text-primary"
                                >
                                    {phone}
                                </a>
                            </li>
                        )}
                        {address && (
                            <li className="flex items-start gap-3">
                                <MapPin className="h-5 w-5 text-primary" />
                                {address}
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Bottom bar */}
            <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-between gap-8 border-t border-white/10 px-6 pt-10 md:flex-row">
                <p className="text-xs text-slate-500">
                    &copy; {new Date().getFullYear()} {getText('copyright')}
                </p>
                <div className="flex items-center gap-8">
                    <div className="flex cursor-pointer items-center gap-3 opacity-50 grayscale transition-all hover:opacity-100 hover:grayscale-0">
                        <ShieldCheck className="h-6 w-6 text-primary" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">
                            {getText('badge1')}
                        </span>
                    </div>
                    <div className="flex cursor-pointer items-center gap-3 opacity-50 grayscale transition-all hover:opacity-100 hover:grayscale-0">
                        <Award className="h-6 w-6 text-brand-yellow" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">
                            {getText('badge2')}
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    )
}
